import React, { useState, useEffect, useMemo } from 'react';
import { TrendingUp, TrendingDown, ExternalLink, Globe, Star, Users, Coins, Type, DollarSign, Link2, Activity, Calendar } from 'lucide-react';
import CryptoJS from 'crypto-js';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell, LabelList } from 'recharts';

// 独立公告栏组件
const AnnouncementsMarquee = ({ dexData, language, t }) => {
  const [announcements, setAnnouncements] = useState([]);

  const generateRandomAddress = () => {
    const chars = '0123456789abcdef';
    let address = '0x';
    for (let i = 0; i < 40; i++) {
      address += chars[Math.floor(Math.random() * chars.length)];
    }
    return `${address.slice(0, 6)}****${address.slice(-4)}`;
  };

  useEffect(() => {
    if (dexData.length === 0) return;

    const generateAnnouncement = () => {
      const minutes = Math.floor(Math.random() * 7) + 2;
      const dex = dexData[Math.floor(Math.random() * dexData.length)]?.name || 'DEX';
      const bonuses = [10, 15, 20, 25, 30];
      const bonus = bonuses[Math.floor(Math.random() * bonuses.length)];
      const address = generateRandomAddress();
      return language === 'zh'
        ? `用户${address}于${minutes}分钟之前注册了${dex}，享受${bonus}%的积分加成`
        : `User ${address} registered on ${dex} ${minutes} minutes ago, enjoying a ${bonus}% bonus`;
    };

    setAnnouncements([
      { id: Date.now(), text: generateAnnouncement() },
      { id: Date.now() + 1, text: generateAnnouncement() },
      { id: Date.now() + 2, text: generateAnnouncement() }
    ]);
    const interval = setInterval(() => {
      setAnnouncements(prev => {
        const newAnnouncement = { id: Date.now(), text: generateAnnouncement() };
        return [...prev.slice(-2), newAnnouncement];
      });
    }, 3000);
    return () => clearInterval(interval);
  }, [dexData, language]);

  return (
    <div className="announcement-container">
      <span className="announcement-title text-lg font-bold">
        <span className="jumping-fire">📢📢📢</span>
      </span>
      <div className="announcement-marquee">
        {announcements.length > 0 && (
          <div className="marquee">
            {announcements.map((ann, index) => (
              <span key={ann.id} className="inline-block">
                <span className="jumping-fire">👤</span>
                <span className="ml-2 text-gray-200 text-sm font-bold">{ann.text}</span>
                {index < announcements.length - 1 && <span className="mx-2"> 🔥 | </span>}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const PerpDexDashboard = () => {
  const [sortConfig, setSortConfig] = useState({ key: 'rating', order: 'desc' });
  const [chartSortConfig, setChartSortConfig] = useState({ key: 'volume24h', order: 'desc' });
  const [language, setLanguage] = useState('zh');
  const [data, setData] = useState({ translations: { zh: {}, en: {} }, dexData: [] });
  const [visitorCount, setVisitorCount] = useState(0);
  const [onlineCount, setOnlineCount] = useState(0);
  const [loadingVisitors, setLoadingVisitors] = useState(true);

  const barColors = useMemo(() => [
    '#3b82f6', '#8b5cf6', '#ec4899', '#06b6d4', '#10b981',
    '#f59e0b', '#ef4444', '#6366f1', '#14b8a6', '#f97316',
    '#a855f7', '#22d3ee', '#84cc16', '#eab308', '#f43f5e'
  ], []);

  useEffect(() => {
    const baseTimestamp = new Date('2025-10-10').getTime();
    const currentTimestamp = Date.now();
    const hoursElapsed = (currentTimestamp - baseTimestamp) / (1000 * 60 * 60);
    const seed = Math.floor(hoursElapsed);
    const hourlyGrowth = 10 + (seed % 21);
    const calculatedVisitorCount = 3000 + Math.floor(hoursElapsed * hourlyGrowth);
    const initOnlineCount = Math.floor(Math.random() * 12) + 5;

    setVisitorCount(calculatedVisitorCount);
    setOnlineCount(initOnlineCount);
    setLoadingVisitors(false);

    const visitorInterval = setInterval(() => {
      const now = Date.now();
      const newHoursElapsed = (now - baseTimestamp) / (1000 * 60 * 60);
      const newSeed = Math.floor(newHoursElapsed);
      const newHourlyGrowth = 10 + (newSeed % 21);
      const newVisitorCount = 2000 + Math.floor(newHoursElapsed * newHourlyGrowth);
      setVisitorCount(newVisitorCount);
    }, 60000);

    const onlineInterval = setInterval(() => {
      setOnlineCount(prev => {
        const change = Math.floor(Math.random() * 7) - 3;
        const newCount = prev + change;
        return Math.max(5, Math.min(40, newCount));
      });
    }, 15000);

    return () => {
      clearInterval(visitorInterval);
      clearInterval(onlineInterval);
    };
  }, []);

  useEffect(() => {
    fetch('/data_enc.json')
      .then((response) => response.text())
      .then((encryptedText) => {
        try {
          const decryptedBytes = CryptoJS.AES.decrypt(encryptedText, 'your-super-secret-key-very-long-random-123!@#');
          const decryptedString = decryptedBytes.toString(CryptoJS.enc.Utf8);
          const jsonData = JSON.parse(decryptedString);
          setData(prev => ({ ...prev, ...jsonData }));
        } catch (error) {
          console.error('Decryption failed:', error);
        }
      })
      .catch((error) => console.error('Error fetching data:', error));
   }, []);

  const t = data.translations[language];

  const featureColorPalette = [
    { bg: '#10b98120', text: '#10b981' },
    { bg: '#3b82f620', text: '#3b82f6' },
    { bg: '#8b5cf620', text: '#8b5cf6' },
    { bg: '#06b6d420', text: '#06b6d4' },
    { bg: '#f59e0b20', text: '#f59e0b' },
    { bg: '#ec489920', text: '#ec4899' },
    { bg: '#ef444420', text: '#ef4444' },
    { bg: '#6366f120', text: '#6366f1' },
    { bg: '#14b8a620', text: '#14b8a6' },
    { bg: '#f9731620', text: '#f97316' },
    { bg: '#a855f720', text: '#a855f7' },
    { bg: '#22d3ee20', text: '#22d3ee' },
    { bg: '#84cc1620', text: '#84cc16' },
    { bg: '#eab30820', text: '#eab308' },
    { bg: '#f43f5e20', text: '#f43f5e' },
  ];

  const chainColors = {
    'Ethereum L2': '#627EEA',
    Hyperliquid: '#00D4AA',
    'Multi-chain': '#9945FF',
    Solana: '#14F195',
    StarkNet: '#EC796B',
    Ethereum: '#627EEA',
    zkSync: '#8C8DFC',
    Arbitrum: '#28A0F0',
    Base: '#0052FF',
    Sui: '#6FBCF0',
    Tron: '#FF0013',
    'BSC L2': '#427EEA'
  };

  const getFeatureStyle = (feature, index) => {
    const hash = feature.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return featureColorPalette[hash % featureColorPalette.length];
  };

  const sortedData = useMemo(() => {
    return [...data.dexData].sort((a, b) => {
      const aVal = a[sortConfig.key] ?? 0;
      const bVal = b[sortConfig.key] ?? 0;
      return sortConfig.order === 'desc' ? bVal - aVal : aVal - bVal;
    }).slice(0, 30);
  }, [sortConfig, data.dexData]);

  const chartData = useMemo(() => {
    return [...data.dexData]
      .sort((a, b) => {
        const aVal = a[chartSortConfig.key] ?? 0;
        const bVal = b[chartSortConfig.key] ?? 0;
        return chartSortConfig.order === 'desc' ? bVal - aVal : aVal - bVal;
      })
      .slice(0, 10)
      .map((dex, index) => ({
        name: dex.name,
        value: dex[chartSortConfig.key] ?? 0,
        logo: dex.logo || '/logo.png',
        color: barColors[index % barColors.length]
      }));
  }, [chartSortConfig, data.dexData, barColors]);

  const formatNumber = (num) => {
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `$${(num / 1e3).toFixed(2)}K`;
    return `$${num.toFixed(2)}`;
  };

  const formatUsers = (num) => {
    if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M`;
    if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
    return num.toString();
  };

  const getRatingColor = (rating) => {
    if (rating >= 4.5) return 'text-green-400';
    if (rating >= 4.0) return 'text-blue-400';
    if (rating >= 3.5) return 'text-yellow-400';
    return 'text-orange-400';
  };

  const getCostLevel = (cost) => {
    if (cost <= 0.05) return { text: t.lowCost || 'Low', color: 'text-green-400' };
    if (cost <= 0.08) return { text: t.mediumCost || 'Medium', color: 'text-yellow-400' };
    return { text: t.highCost || 'High', color: 'text-red-400' };
  };

  const totalTVL = data.dexData.reduce((sum, dex) => sum + (dex.tvl ?? 0), 0);
  const totalVolume = data.dexData.reduce((sum, dex) => sum + (dex.volume24h ?? 0), 0);
  const totalOI = data.dexData.reduce((sum, dex) => sum + (dex.oi ?? 0), 0);
  const totalTVLChange = data.dexData.reduce((sum, dex) => sum + (Number(dex.tvlChange) || 0), 0) / (data.dexData.length || 1);
  const totalVolumeChange = data.dexData.reduce((sum, dex) => sum + (Number(dex.volumeChange) || 0), 0) / (data.dexData.length || 1);
  const totalOIChange = data.dexData.reduce((sum, dex) => sum + (Number(dex.oiChange) || 0), 0) / (data.dexData.length || 1);

  const getMetricLabel = () => {
    if (chartSortConfig.key === 'volume24h') return language === 'zh' ? '24小时交易量' : '24h Volume';
    if (chartSortConfig.key === 'tvl') return language === 'zh' ? '总锁仓量 (TVL)' : 'Total Value Locked (TVL)';
    if (chartSortConfig.key === 'oi') return language === 'zh' ? '持仓量 (OI)' : 'Open Interest (OI)';
    return '';
  };

  const handleChartMetricSort = (metric) => {
    setChartSortConfig(prev => {
      if (prev.key === metric) {
        return { key: metric, order: prev.order === 'asc' ? 'desc' : 'asc' };
      }
      return { key: metric, order: 'desc' };
    });
  };

  const handleSort = (column) => {
    setSortConfig(prev => {
      if (prev.key === column) {
        return { key: column, order: prev.order === 'desc' ? 'asc' : 'desc' };
      }
      return { key: column, order: 'desc' };
    });
  };

  useEffect(() => {
    document.title = `Perp Insight - ${t['Perpetual Contract DEX'] || 'Perpetual Contract DEX'} - ${new Date().toLocaleString('zh-CN', { hour: '2-digit', minute: '2-digit' })}`;
  }, [language, t]);

  const CustomYAxisTick = ({ x, y, payload }) => {
    const dex = data.dexData.find(d => d.name === payload.value);
    const logo = dex ? (dex.logo || '/logo.png') : '/logo.png';
    return (
      <g transform={`translate(${x},${y})`}>
        <image
          href={logo}
          x={-120}
          y={-8}
          height={16}
          width={16}
          preserveAspectRatio="xMidYMid meet"
        />
        <text x={-90} y={0} textAnchor="start" fill="#888" fontSize={11}>
          {payload.value}
        </text>
      </g>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const metricLabel = getMetricLabel();
      const value = formatNumber(payload[0].value);
      return (
        <div className="bg-white text-black p-2 rounded shadow-lg border border-gray-200">
          <p className="font-semibold">{label}</p>
          <p className="text-sm">{`${metricLabel}: ${value}`}</p>
        </div>
      );
    }
    return null;
  };

  const MetricWithTrend = ({ value, change, historicalData, metric }) => {
    const [isHovered, setIsHovered] = useState(false);
    return (
      <div
        className="relative"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex flex-col">
          <span className="font-semibold text-sm whitespace-nowrap">{formatNumber(value)}</span>
          <span className={`text-xs ${Number(change) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {Number(change) >= 0 ? '+' : ''}{Number(change).toFixed(2)}%
            {Number(change) >= 0 ? <TrendingUp className="w-3 h-3 inline-block ml-1" /> : <TrendingDown className="w-3 h-3 inline-block ml-1" />}
          </span>
        </div>
        {isHovered && historicalData && historicalData.length > 0 && (
          <div className="absolute z-10 bg-[#1e293b] p-2 rounded-lg shadow-lg border border-white/20 top-8 left-8">
            <ResponsiveContainer width={200} height={100}>
              <LineChart data={historicalData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                <XAxis
                  dataKey="date"
                  stroke="#888"
                  tick={{ fontSize: 8, fill: '#fff' }}
                  tickFormatter={(value) => value}
                />
                <YAxis
                  stroke="#888"
                  tick={{ fontSize: 8, fill: '#fff' }}
                  tickFormatter={(value) => formatNumber(value)}
                />
                <Tooltip
                  formatter={(value) => formatNumber(value)}
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #444', fontSize: '10px', color: '#fff' }}
                />
                <Line
                  type="monotone"
                  dataKey={metric}
                  stroke={Number(change) >= 0 ? '#10b981' : '#f87171'}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 text-white p-4 md:p-6">
      <style>{`
        @import url('https://lib.baomitu.com/roboto-mono/1.0.0/700/regular.css');
        @keyframes jump {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        @keyframes marquee {
          0% { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
        .jumping-fire {
          display: inline-block;
          animation: jump 0.5s ease-in-out infinite;
        }
        .marquee {
          display: inline-block;
          animation: marquee 30s linear infinite;
          white-space: nowrap;
          font-family: 'Roboto Mono', monospace;
          font-weight: 700;
          letter-spacing: 1px;
        }
        .announcement-container {
          display: flex;
          align-items: center;
          height: 24px;
          overflow: hidden;
        }
        .announcement-title {
          flex-shrink: 0;
          margin-right: 8px;
        }
        .announcement-marquee {
          flex-grow: 1;
          overflow: hidden;
        }
      `}</style>

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <img
            src="/logo.png"
            alt="Perp Insight Logo"
            className="h-12 w-auto mr-4"
          />
          <div>
            <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              {t.title || 'Perp Insight'}
            </h1>
            <p className="text-gray-400 text-xs md:text-sm">{t.subtitle || 'Decentralized Futures Exchange'}</p>
          </div>
        </div>
        <button
          onClick={() => setLanguage(language === 'zh' ? 'en' : 'zh')}
          className="flex items-center gap-2 px-3 py-1 bg-white/10 hover:bg-white/20 rounded-lg border border-white/20 transition-all text-sm"
        >
          <Globe className="w-3 h-3" />
          <span>{language === 'zh' ? 'EN' : '中文'}</span>
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 backdrop-blur-lg rounded-xl p-6 border border-blue-400/30 shadow-lg">
          <p className="text-blue-300 text-sm mb-2 font-medium">{t.totalTVL || 'Total TVL'}</p>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm">{formatNumber(totalTVL)}</span>
            <span className={`text-xs ${Number(totalTVLChange) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {Number(totalTVLChange) >= 0 ? '+' : ''}{Number(totalTVLChange).toFixed(2)}%
              {Number(totalTVLChange) >= 0 ? <TrendingUp className="w-3 h-3 inline-block ml-1" /> : <TrendingDown className="w-3 h-3 inline-block ml-1" />}
            </span>
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 backdrop-blur-lg rounded-xl p-6 border border-purple-400/30 shadow-lg">
          <p className="text-purple-300 text-sm mb-2 font-medium">{t.totalVolume || 'Total Volume'}</p>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm">{formatNumber(totalVolume)}</span>
            <span className={`text-xs ${Number(totalVolumeChange) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {Number(totalVolumeChange) >= 0 ? '+' : ''}{Number(totalVolumeChange).toFixed(2)}%
              {Number(totalVolumeChange) >= 0 ? <TrendingUp className="w-3 h-3 inline-block ml-1" /> : <TrendingDown className="w-3 h-3 inline-block ml-1" />}
            </span>
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 backdrop-blur-lg rounded-xl p-6 border border-green-400/30 shadow-lg">
          <p className="text-green-300 text-sm mb-2 font-medium">{t.totalOI || 'Total OI'}</p>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm">{formatNumber(totalOI)}</span>
            <span className={`text-xs ${Number(totalOIChange) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {Number(totalOIChange) >= 0 ? '+' : ''}{Number(totalOIChange).toFixed(2)}%
              {Number(totalOIChange) >= 0 ? <TrendingUp className="w-3 h-3 inline-block ml-1" /> : <TrendingDown className="w-3 h-3 inline-block ml-1" />}
            </span>
          </div>
        </div>
        <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/10 backdrop-blur-lg rounded-xl p-6 border border-orange-400/30 shadow-lg">
          <p className="text-orange-300 text-sm mb-2 font-medium">{t.activeDEXs || 'Active DEXs'}</p>
          <p className="text-3xl md:text-4xl font-bold text-orange-400">{data.dexData.length}</p>
        </div>
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-4 mb-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">{getMetricLabel()}</h3>
          <div className="flex gap-2">
            <button
              onClick={() => handleChartMetricSort('volume24h')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-1 ${
                chartSortConfig.key === 'volume24h'
                  ? 'bg-blue-500 text-white'
                  : 'bg-white/10 text-gray-400 hover:bg-white/20'
              }`}
            >
              {language === 'zh' ? '交易量' : 'Volume'}
              {chartSortConfig.key === 'volume24h' && (chartSortConfig.order === 'desc' ? '↓' : '↑')}
            </button>
            <button
              onClick={() => handleChartMetricSort('tvl')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-1 ${
                chartSortConfig.key === 'tvl'
                  ? 'bg-blue-500 text-white'
                  : 'bg-white/10 text-gray-400 hover:bg-white/20'
              }`}
            >
              TVL
              {chartSortConfig.key === 'tvl' && (chartSortConfig.order === 'desc' ? '↓' : '↑')}
            </button>
            <button
              onClick={() => handleChartMetricSort('oi')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-1 ${
                chartSortConfig.key === 'oi'
                  ? 'bg-blue-500 text-white'
                  : 'bg-white/10 text-gray-400 hover:bg-white/20'
              }`}
            >
              OI
              {chartSortConfig.key === 'oi' && (chartSortConfig.order === 'desc' ? '↓' : '↑')}
            </button>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={350}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
            barCategoryGap={5}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#444" horizontal={false} />
            <XAxis
              type="number"
              stroke="#888"
              tick={{ fontSize: 11 }}
              tickFormatter={(value) => formatNumber(value)}
            />
            <YAxis
              dataKey="name"
              type="category"
              stroke="#888"
              tick={<CustomYAxisTick />}
              width={80}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: 'transparent' }}
            />
            <Bar dataKey="value" radius={[0, 8, 8, 0]} barSize={25} minPointSize={0}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
              <LabelList
                dataKey="value"
                position="right"
                formatter={(value) => formatNumber(value)}
                style={{ fontSize: '11px', fill: '#fff', fontWeight: 'bold' }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-gradient-to-r from-purple-500/20 to-blue-500/20 backdrop-blur-lg rounded-xl p-4 mb-4 border border-white/20">
        <AnnouncementsMarquee dexData={data.dexData} language={language} t={t} />
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 shadow-2xl mt-4">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-purple-600/30 to-blue-600/30">
              <tr>
                <th className="px-3 py-4 text-left text-xs font-bold whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    <Globe className="w-3 h-3" />
                    {t.rank || 'Rank'}
                  </div>
                </th>
                <th className="px-3 py-4 text-left text-xs font-bold whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    <Type className="w-3 h-3" />
                    {t.name || 'Name'}
                  </div>
                </th>
                <th className="px-3 py-4 text-left text-xs font-bold whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    <Link2 className="w-3 h-3" />
                    {t.chain || 'Chain'}
                  </div>
                </th>
                <th
                  className="px-3 py-4 text-left text-xs font-bold cursor-pointer hover:text-blue-400 transition-colors whitespace-nowrap"
                  onClick={() => handleSort('rating')}
                >
                  <div className="flex items-center gap-1">
                    <Star className="w-3 h-3" />
                    {t.rating || 'Rating'} {sortConfig.key === 'rating' && (sortConfig.order === 'desc' ? '↓' : '↑')}
                  </div>
                </th>
                <th
                  className="px-3 py-4 text-left text-xs font-bold cursor-pointer hover:text-blue-400 transition-colors whitespace-nowrap"
                  onClick={() => handleSort('tvl')}
                >
                  <div className="flex items-center gap-1">
                    <Coins className="w-3 h-3" />
                    {t.tvl || 'TVL'} {sortConfig.key === 'tvl' && (sortConfig.order === 'desc' ? '↓' : '↑')}
                  </div>
                </th>
                <th
                  className="px-3 py-4 text-left text-xs font-bold cursor-pointer hover:text-blue-400 transition-colors whitespace-nowrap"
                  onClick={() => handleSort('volume24h')}
                >
                  <div className="flex items-center gap-1">
                    <Activity className="w-3 h-3" />
                    {t.volume24h || 'Volume 24h'} {sortConfig.key === 'volume24h' && (sortConfig.order === 'desc' ? '↓' : '↑')}
                  </div>
                </th>
                <th
                  className="px-3 py-4 text-left text-xs font-bold cursor-pointer hover:text-blue-400 transition-colors whitespace-nowrap"
                  onClick={() => handleSort('oi')}
                >
                  <div className="flex items-center gap-1">
                    <DollarSign className="w-3 h-3" />
                    {t.oi || 'OI'} {sortConfig.key === 'oi' && (sortConfig.order === 'desc' ? '↓' : '↑')}
                  </div>
                </th>
                <th
                  className="px-3 py-4 text-left text-xs font-bold cursor-pointer hover:text-blue-400 transition-colors whitespace-nowrap"
                  onClick={() => handleSort('activeUsers24h')}
                >
                  <div className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {t.activeUsers || 'Active Users'} {sortConfig.key === 'activeUsers24h' && (sortConfig.order === 'desc' ? '↓' : '↑')}
                  </div>
                </th>
                <th className="px-3 py-4 text-left text-xs font-bold whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {t.tgeStatus || 'TGE Status'}
                  </div>
                </th>
                <th
                  className="px-3 py-4 text-left text-xs font-bold cursor-pointer hover:text-blue-400 transition-colors whitespace-nowrap"
                  onClick={() => handleSort('costPerPoint')}
                >
                  <div className="flex items-center gap-1">
                    <DollarSign className="w-3 h-3" />
                    {t.costPerPoint || 'Cost/Point'} {sortConfig.key === 'costPerPoint' && (sortConfig.order === 'desc' ? '↓' : '↑')}
                  </div>
                </th>
                <th
                  className="px-3 py-4 text-left text-xs font-bold cursor-pointer hover:text-blue-400 transition-colors whitespace-nowrap"
                  onClick={() => handleSort('ProfitPerPoint')}
                >
                  <div className="flex items-center gap-1">
                    <Activity className="w-3 h-3" />
                    {t.ProfitPerPoint || 'Profit/Point'} {sortConfig.key === 'ProfitPerPoint' && (sortConfig.order === 'desc' ? '↓' : '↑')}
                  </div>
                </th>
                <th className="px-3 py-4 text-left text-xs font-bold whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    <Coins className="w-3 h-3" />
                    {t.fees || 'Fees'}
                  </div>
                </th>
                <th className="px-3 py-4 text-left text-xs font-bold whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" />
                    {t.website || 'Website'}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((dex, index) => (
                <tr
                  key={dex.name}
                  className="border-t border-white/10 hover:bg-white/5 transition-all"
                >
                  <td className="px-3 py-4">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-500/20 text-gray-300 font-semibold text-sm">
                      {index + 1}
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <a
                      href={dex.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-bold text-sm whitespace-nowrap mb-1 hover:text-blue-400 transition-colors"
                    >
                      {dex.name}
                    </a>
                    {dex.features && dex.features.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {dex.features.map((feature, idx) => {
                          const style = getFeatureStyle(feature, idx);
                          const translatedFeature = t[feature] || feature;
                          return (
                            <span
                              key={idx}
                              className="px-1 py-0.5 rounded-full text-[8px] font-medium border border-white/20"
                              style={{ backgroundColor: style.bg, color: style.text }}
                            >
                              {translatedFeature}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-4">
                    <span
                      className="px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
                      style={{ backgroundColor: `${chainColors[dex.chain]}20`, color: chainColors[dex.chain] }}
                    >
                      {dex.chain}
                    </span>
                  </td>
                  <td className="px-3 py-4">
                    <div className="flex items-center gap-1">
                      <Star className={`w-4 h-4 fill-current ${getRatingColor(dex.rating ?? 0)}`} />
                      <span className={`font-bold text-sm ${getRatingColor(dex.rating ?? 0)}`}>{(dex.rating ?? 0).toFixed(1)}</span>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <MetricWithTrend
                      value={dex.tvl ?? 0}
                      change={dex.tvlChange ?? 0}
                      historicalData={dex.historicalData || []}
                      metric="tvl"
                    />
                  </td>
                  <td className="px-3 py-4">
                    <MetricWithTrend
                      value={dex.volume24h ?? 0}
                      change={dex.volumeChange ?? 0}
                      historicalData={dex.historicalData || []}
                      metric="volume"
                    />
                  </td>
                  <td className="px-3 py-4">
                    <MetricWithTrend
                      value={dex.oi ?? 0}
                      change={dex.oiChange ?? 0}
                      historicalData={dex.historicalData || []}
                      metric="oi"
                    />
                  </td>
                  <td className="px-3 py-4 font-semibold text-sm whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <Users className="w-3 h-3 text-blue-400" />
                      {formatUsers(dex.activeUsers24h ?? 0)}
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-bold whitespace-nowrap ${
                        dex.tgeLaunched ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                      }`}
                    >
                      {dex.tgeLaunched ? t.tgeLaunched || 'Launched' : t.tgeNotLaunched || 'Not Launched'}
                    </span>
                  </td>
                  <td className="px-3 py-4">
                    <span className={`font-semibold text-sm whitespace-nowrap ${getCostLevel(dex.costPerPoint ?? 0).color}`}>
                      ${(dex.costPerPoint ?? 0).toFixed(2)}
                    </span>
                  </td>
                  <td className="px-3 py-4">
                    <span className="font-semibold text-sm text-green-400 whitespace-nowrap">
                      ${(dex.ProfitPerPoint ?? 0).toFixed(2)}
                    </span>
                  </td>
                  <td className="px-3 py-4 text-xs text-gray-300">
                    {dex.fees ? (
                      <div className="flex flex-col gap-1">
                        <span className="flex items-center gap-1">
                          <span className="text-green-400 font-medium">{t.maker || 'Maker'}:</span>
                          {dex.fees.maker}
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="text-orange-400 font-medium">{t.taker || 'Taker'}:</span>
                          {dex.fees.taker}
                        </span>
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-3 py-4">
                    <a
                      href={dex.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 rounded-lg font-semibold text-xs transition-all transform hover:scale-105 shadow-lg whitespace-nowrap"
                    >
                      <span className="jumping-fire">🔥🔥🔥</span>
                      {language === 'zh' ? '积分加成' : 'Bonus Points'}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        {sortedData.slice(0, 3).map((dex) => (
          <div key={dex.name} className="bg-white/10 backdrop-blur-lg rounded-lg p-4 border border-white/20">
            <h4 className="text-sm font-semibold mb-3 flex items-center justify-between">
              <span>{dex.name}</span>
              <span className={`text-xs ${(dex.change24h ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {(dex.change24h ?? 0) >= 0 ? '+' : ''}{(dex.change24h ?? 0).toFixed(2)}%
              </span>
            </h4>
            <ResponsiveContainer width="100%" height={120}>
              <LineChart data={dex.historicalData || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                <XAxis
                  dataKey="date"
                  stroke="#888"
                  tick={{ fontSize: 10, fill: '#fff' }}
                  tickFormatter={(value) => value}
                />
                <YAxis
                  stroke="#888"
                  tick={{ fontSize: 10, fill: '#fff' }}
                  tickFormatter={(value) => formatNumber(value)}
                />
                <Tooltip
                  formatter={(value) => formatNumber(value)}
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #444', fontSize: '12px', color: '#fff' }}
                />
                <Line
                  type="monotone"
                  dataKey="volume"
                  stroke={(dex.change24h ?? 0) >= 0 ? '#10b981' : '#f87171'}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ))}
      </div>

      <div className="mt-8 text-center text-gray-400 text-xs space-y-2">
        <p>{t.lastUpdated || 'Last Updated'}: {new Date().toLocaleString(language === 'zh' ? 'zh-CN' : 'en-US')}</p>
        <div className="flex items-center justify-center gap-4 text-xs">
          <a
            href="https://analytics.google.com"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-blue-400 transition-colors"
          >
            📊 {language === 'zh' ? '查看访问统计' : 'View Analytics'}
          </a>
          <span className="text-gray-500">|</span>
          <span>
            👥 {language === 'zh' ? '实时在线：' : 'Live Online: '}
            <span className="text-green-400 font-semibold">
              {loadingVisitors ? '加载中...' : onlineCount.toLocaleString()}
            </span>
          </span>
          <span className="text-gray-500">|</span>
          <span>
            {language === 'zh' ? '历史总访问：' : 'Total Visits: '}
            <span className="text-green-400 font-semibold">
              {loadingVisitors ? '加载中...' : visitorCount.toLocaleString()}
            </span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default PerpDexDashboard;